import { Button } from "@/design-system/Button/Button";
import { CheckBox } from "@/design-system/Check/Check";
import Input from "@/design-system/Input/Input";
import { cn } from "@/lib/utils";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { FormEvent, useState } from "react";
import { Link } from "react-router";

import {
  EMAIL_PATTERN,
  type WaitlistForm as FormId,
  type useObjectivesWaitlist,
} from "./useObjectivesWaitlist";

export const WaitlistForm = ({
  form,
  waitlist,
  className,
}: {
  form: FormId;
  waitlist: ReturnType<typeof useObjectivesWaitlist>;
  className?: string;
}) => {
  const { t } = useTranslation("objectives");
  const { state, loggedIn, prefillEmail, submit } = waitlist;
  // Initial value only, so typing over it — or clearing it — sticks.
  const [email, setEmail] = useState(prefillEmail);
  const [invalid, setInvalid] = useState(false);
  // Explicit opt-in: starts unchecked; submitting without it nudges instead.
  const [consented, setConsented] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (state === "joining") return;
    const emailInvalid = !loggedIn && !EMAIL_PATTERN.test(email.trim());
    setInvalid(emailInvalid);
    setNeedsConsent(!consented);
    if (emailInvalid || !consented) return;
    submit(form, email.trim());
  };

  if (state === "joined" || state === "alreadyJoined") {
    return (
      <div
        role="status"
        className={cn(
          "flex min-h-11 items-center justify-center rounded-lg bg-complete/10 px-4 text-center text-sm font-semibold text-complete",
          className,
        )}
      >
        {t(
          state === "joined"
            ? "landing.form.success"
            : "landing.form.alreadyOnList",
        )}
      </div>
    );
  }

  const message = invalid
    ? t("landing.form.invalidEmail")
    : needsConsent
      ? t("landing.form.consentRequired")
      : state === "error"
        ? t("landing.form.error")
        : null;

  return (
    <form noValidate onSubmit={onSubmit} className={className}>
      <div
        className={cn(
          "flex items-center gap-2.5",
          loggedIn && "justify-center",
        )}
      >
        {/* Logged in, the signup is tied to the account's own email. */}
        {!loggedIn && (
          <div className="min-w-0 flex-1">
            <Input
              type="email"
              autoComplete="email"
              aria-label={t("landing.form.emailLabel")}
              aria-invalid={invalid}
              placeholder={t("landing.form.placeholder")}
              value={email}
              onChange={(value) => {
                setEmail(value);
                if (invalid) setInvalid(false);
              }}
              className={cn(invalid && "border-error")}
            />
          </div>
        )}
        <Button
          type="submit"
          variant="primary"
          size="medium"
          disabled={state === "joining" || state === "unknown"}
          className="shrink-0 whitespace-nowrap"
        >
          {t(
            form === "hero"
              ? "landing.form.joinWaitlist"
              : "landing.form.notifyMe",
          )}
        </Button>
      </div>
      <label
        className={cn(
          "mt-3 flex cursor-pointer select-none items-start justify-center gap-2 text-left text-xs text-neutral-400",
        )}
      >
        <input
          type="checkbox"
          checked={consented}
          onChange={() => {
            setConsented((c) => !c);
            setNeedsConsent(false);
          }}
          className="peer sr-only"
        />
        <CheckBox
          checked={consented}
          className={cn(
            "mt-px peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-blue",
            !consented &&
              (needsConsent ? "border-error" : "border-neutral-500"),
          )}
        />
        <span>
          <Trans
            t={t}
            i18nKey="landing.form.consent"
            // New tab, or reading the policy throws away the typed email
            components={[
              <Link
                to="/legal/privacy"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-neutral-600 underline-offset-2 hover:text-neutral-200"
              />,
            ]}
          />
        </span>
      </label>
      {message && (
        <p role="alert" className="mt-2 text-left text-sm text-error">
          {message}
        </p>
      )}
    </form>
  );
};
